import { useTranslation } from 'react-i18next';
import type { DocumentationPillDto } from '@/api/recognition';
import { pickTitle, useDocumentationPills, usePillActivation, PillActivationModal } from './PillDocuments';

// ─────────────────────────────────────────────────────────────────────────────
// Paridad F1 (H-10, H-11) — rail de documentos en el borde derecho + popup.
// FASE 4 (§24.4, RF-137): el rail es DATA-DRIVEN — consume las pills activas visibles
// (`useDocumentationPills`, filtradas por OU en el backend) y las pinta con su ícono/color. Cada
// pill enlaza según su tipo: `documento` (PDF corporativo, resuelto por id contra
// corporate-documents/for-me que re-firma el SAS), `url` (abre en pestaña) o `video`
// (popup con iframe). Si NO hay pills visibles para el usuario (ni globales ni de su OU) el
// rail no pinta nada — el rail LEGADO hardcodeado de 3 pills fijas se retiró: solo debe
// mostrarse lo que un admin configuró explícitamente como pill (§24.4.9 lo sembró como pills
// reales editables/borrables desde el admin; mantener además un fallback fijo en el
// componente contradecía esa regla — un admin que borra/desactiva todas las pills de una OU
// para dejarla sin contenido se encontraba con 3 pills fantasma que no podía tocar).
//
// La consulta de datos y la activación/modal de una pill viven en PillDocuments.tsx (BUG
// 2026-09-04): el overlay "Más → Documentos" del nav móvil frontline (MobileNav.tsx) las
// comparte, para no quedar mostrando un subconjunto distinto de pills que este rail.
//
// Estructura/clases del demo: .home-right-rail, .home-rail-pill (+ -circle),
// .modal-overlay (primitiva F5, antes .home-doc-popup-overlay), .home-doc-popup
// (+ -close/-video/-images/-pdf).
// ─────────────────────────────────────────────────────────────────────────────
export function HomeRightRail() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] ?? 'es';

  const { isFetched, activePills, pills, docUrlById } = useDocumentationPills(lang);

  // Evita parpadeos en cada carga: hasta que la consulta resuelva no se decide nada.
  if (!isFetched) return null;

  // Sin pills visibles para este usuario (ni globales ni de su OU) el rail no pinta nada — nunca cae a
  // contenido fijo ajeno a lo que el admin configuró.
  if (activePills.length === 0) return null;
  return <DataDrivenRail pills={pills} lang={lang} docUrlById={docUrlById} />;
}

/** Rail administrable: una pill por registro activo, con su ícono/color y destino por tipo. */
function DataDrivenRail({
  pills, lang, docUrlById,
}: Readonly<{
  pills: DocumentationPillDto[];
  lang: string;
  docUrlById: Map<string, string | null>;
}>) {
  const viewer = usePillActivation(docUrlById);

  return (
    <>
      <div className="home-right-rail">
        {pills.map((pill) => (
          <button
            key={pill.id}
            type="button"
            className="home-rail-pill"
            // El color de la pill configurada va en la PASTILLA (como los modificadores del rail
            // legado `--tips/--legales/--instructivo`), no en el círculo interior. Antes se pintaba
            // el círculo y la pastilla quedaba transparente: las pills salían como circulitos
            // flotando (se veía "mal" con sesión) en vez del look pleno del legado (incógnito).
            style={{ background: pill.color }}
            aria-label={pickTitle(pill.titulo, lang)}
            title={pickTitle(pill.titulo, lang)}
            onClick={() => viewer.activate(pill)}
          >
            <span className="home-rail-pill-circle">
              <i className={`fas ${pill.icono}`} aria-hidden="true" />
            </span>
          </button>
        ))}
      </div>

      <PillActivationModal viewer={viewer} lang={lang} />
    </>
  );
}
